import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Chip, Input, addToast, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react";
import { Download, RefreshCw, Search, MoreVertical, Trash } from "lucide-react";
import axios from "axios";
import clsx from "clsx";

import { useFooter } from "../components/layout/FooterContext";
import AppButtonHome from "../components/common/AppButtonHome";
import { getAllTranscriptionsAsync } from "../redux/transcription";

const STATUS_LABELS = {
  queued: "Warteschlange",
  processing: "In Bearbeitung",
  completed: "Fertig",
  failed: "Fehlgeschlagen",
  none: "Nicht erstellt",
  error: "Fehler",
};

const parseJsonMaybe = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === "string") {
      try {
        return JSON.parse(parsed);
      } catch (e) {
        return parsed;
      }
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

const collectSessionIds = (node, ids) => {
  if (!node) return;
  if (Array.isArray(node)) {
    node.forEach((item) => collectSessionIds(item, ids));
    return;
  }
  if (typeof node !== "object") return;
  Object.entries(node).forEach(([key, value]) => {
    if (key === "sessionId" || key === "session_id" || key === "sessionID") {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        ids.add(String(value));
      }
    }
    if (key === "sessionIds" || key === "session_ids") {
      (Array.isArray(value) ? value : [value]).forEach((id) => {
        if (id !== undefined && id !== null && String(id).trim() !== "") {
          ids.add(String(id));
        }
      });
    }
    collectSessionIds(value, ids);
  });
};

const extractSessionIds = (summary) => {
  const ids = new Set();
  if (summary?.transcription_id) {
    ids.add(String(summary.transcription_id));
  }
  const parsedInput = parseJsonMaybe(summary?.input_json);
  if (parsedInput) {
    collectSessionIds(parsedInput, ids);
  }
  return Array.from(ids).sort((a, b) => Number(a) - Number(b));
};

const ArticleSummaries = () => {
  const { setFooterContent, setTitle } = useFooter();
  const dispatch = useDispatch();
  const { transcriptions } = useSelector((state) => state.transcriptions);
  const transcriptionsList = Array.isArray(transcriptions) ? transcriptions : [];

  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [summaryToDelete, setSummaryToDelete] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const searchTimerRef = useRef(null);

  useEffect(() => {
    setTitle("Artikel");
    setFooterContent(null);
    return () => setFooterContent(null);
  }, [setFooterContent, setTitle]);

  useEffect(() => {
    dispatch(getAllTranscriptionsAsync());
  }, [dispatch]);

  const loadSummaries = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append("search", search);
      const res = await axios.get(
        `transcriptions_poc_000113_1_0_0/summarize-articles-list-paginated?${params}`
      );
      setSummaries(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotalItems(res.data?.total || 0);
      setCurrentPage(res.data?.page || 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummaries(1, "");
  }, []);

  // Debounced server-side search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadSummaries(1, searchText);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchText]);

  const availableStatuses = useMemo(() => {
    const statuses = new Set();
    summaries.forEach((summary) => {
      const status = summary?.status || "none";
      statuses.add(status);
    });
    return Array.from(statuses).sort();
  }, [summaries]);

  const filteredSummaries = useMemo(() => {
    let filtered = summaries.filter(
      (summary) => summary && summary.status && summary.status !== "none"
    );
    if (selectedStatuses.size > 0) {
      filtered = filtered.filter((summary) =>
        selectedStatuses.has(summary?.status || "none")
      );
    }
    return filtered;
  }, [summaries, selectedStatuses]);

  const handleDownload = async (summary) => {
    try {
      const res = await axios.get(
        `transcriptions_poc_000113_1_0_0/summarize-articles-download/${summary.id}`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Artikel_${summary.name || `summary_${summary.id}`}.docx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      addToast({
        title: "Download fehlgeschlagen",
        color: "danger",
      });
    }
  };

  const handleDelete = async () => {
    if (!summaryToDelete) return;
    try {
      await axios.delete(
        `transcriptions_poc_000113_1_0_0/summarize-articles/${summaryToDelete.id}`
      );
      addToast({ title: "Zusammenfassung gelöscht", color: "success" });
      loadSummaries(currentPage, searchText);
    } catch (err) {
      addToast({ title: "Löschen fehlgeschlagen", color: "danger" });
    } finally {
      setIsDeleteOpen(false);
      setSummaryToDelete(null);
    }
  };

  return (
    <div className="h-[82vh] overflow-auto bg-transparent rounded-2xl px-3 border-0">
      <div className="p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 w-full md:w-1/2">
            <Input
              variant="bordered"
              startContent={<Search className="w-4 h-4 text-gray-500" />}
              placeholder="Sitzung suchen..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button
              isIconOnly
              variant="light"
              onPress={() => loadSummaries(currentPage, searchText)}
              isDisabled={loading}
              title="Aktualisieren"
            >
              <RefreshCw className={clsx("w-4 h-4", loading && "animate-spin")} />
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {availableStatuses.map((status) => (
              <Chip
                key={status}
                variant="bordered"
                className={clsx(
                  "cursor-pointer text-xs px-2 py-0.5 border-0",
                  selectedStatuses.has(status) ? "bg-secondaryContainer" : "bg-gray-100"
                )}
                onClick={() => {
                  const next = new Set(selectedStatuses);
                  if (next.has(status)) {
                    next.delete(status);
                  } else {
                    next.add(status);
                  }
                  setSelectedStatuses(next);
                }}
              >
                {STATUS_LABELS[status] || status}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      <ul className="divide-y divide-gray-200 p-0 mt-4 bg-white rounded-2xl shadow-sm border border-gray-100">
        {filteredSummaries.length === 0 ? (
          <li className="px-6 py-6 text-sm text-gray-500">Keine Einträge gefunden</li>
        ) : (
          filteredSummaries.map((summary) => {
            const statusLabel = STATUS_LABELS[summary.status] || summary.status;
            const sessionIds = extractSessionIds(summary);
            const sessionNames = sessionIds.map((id) => {
              const session = transcriptionsList.find(
                (t) => String(t.id) === String(id)
              );
              return session?.name || `Session ${id}`;
            });

            return (
              <li key={summary.id} className="hover:bg-secondaryContainer/40 transition-colors">
                <div className="px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      {summary.name || `Artikelzusammenfassung ${summary.id}`}
                      {summary.status !== "completed" && (
                        <Chip
                          variant="bordered"
                          className="text-xs px-2 py-0.5 bg-gray-100 border-0"
                        >
                          {statusLabel}
                        </Chip>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2">
                      {sessionIds.length > 0 && (
                        <span>
                          Sitzungen: {sessionNames.join(", ")}
                        </span>
                      )}
                      {summary.updated_at && (
                        <span>Aktualisiert: {summary.updated_at.split("T")[0]}</span>
                      )}
                      {summary.error_message && (
                        <span className="text-danger">Fehler: {summary.error_message}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <AppButtonHome
                      onClick={() => handleDownload(summary)}
                      width="w-[150px]"
                      roundedFull="rounded-[16px]"
                      disabled={summary.status !== "completed" || !summary.has_docx}
                      className="hidden md:inline-flex"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </AppButtonHome>
                    <Button
                      isIconOnly
                      variant="light"
                      className="md:hidden"
                      isDisabled={summary.status !== "completed" || !summary.has_docx}
                      onPress={() => handleDownload(summary)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Dropdown className="text-onSurface bg-surfaceContainer p-0 border-0">
                      <DropdownTrigger>
                        <Button
                          isIconOnly
                          className="h-9 w-9 bg-surfaceVariant hover:bg-onSurfaceVariant rounded-full group"
                        >
                          <MoreVertical className="w-4 h-4 text-onSurface group-hover:text-white" />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        className="p-0"
                        aria-label="Summary actions"
                        onAction={(key) => {
                          if (key === "delete") {
                            setSummaryToDelete(summary);
                            setIsDeleteOpen(true);
                          }
                        }}
                      >
                        <DropdownItem
                          key="delete"
                          startContent={<Trash className="w-4 h-4" />}
                          className="text-sm p-4 py-3 rounded-xl"
                          color="danger"
                          textValue="Zusammenfassung löschen"
                        >
                          Löschen
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              </li>
            );
          })
        )}
      </ul>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-4">
          <Button
            size="sm"
            variant="flat"
            isDisabled={currentPage <= 1}
            onPress={() => loadSummaries(currentPage - 1, searchText)}
          >
            Zurück
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((item, idx) =>
              item === "..." ? (
                <span key={`dots-${idx}`} className="px-1 text-sm text-default-400">...</span>
              ) : (
                <Button
                  key={item}
                  size="sm"
                  variant={item === currentPage ? "solid" : "flat"}
                  className={item === currentPage ? "bg-secondaryContainer text-onSecondaryContainer" : ""}
                  onPress={() => loadSummaries(item, searchText)}
                >
                  {item}
                </Button>
              )
            )}
          <Button
            size="sm"
            variant="flat"
            isDisabled={currentPage >= totalPages}
            onPress={() => loadSummaries(currentPage + 1, searchText)}
          >
            Weiter
          </Button>
          <span className="text-xs text-default-400 ml-2">
            {totalItems} Einträge
          </span>
        </div>
      )}

      <Modal isOpen={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Zusammenfassung löschen</ModalHeader>
              <ModalBody>
                <p>
                  Möchten Sie die Zusammenfassung{" "}
                  <strong>{summaryToDelete?.name || `#${summaryToDelete?.id}`}</strong>{" "}
                  wirklich löschen?
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="bordered" radius="full" onPress={() => {
                  onClose();
                  setSummaryToDelete(null);
                }}>
                  Abbrechen
                </Button>
                <Button color="danger" onPress={handleDelete}>
                  Löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ArticleSummaries;
